import * as React from "react"

// ═══════════════════════════════════════════════════════════════════════════
// PAYVIT — UNIFIED TOKENS
//
// Nothing here is invented. Every value is either lifted verbatim from
// PayvitDashboard.tsx / transfe_flow.tsx, or is the nearest step on a scale
// built to absorb the loose values already in use. The point of this pass is
// not new tokens — it is that the existing ones stop being bypassed.
// ═══════════════════════════════════════════════════════════════════════════

/** Verbatim from PayvitDashboard.tsx. Unchanged. */
export const C = {
    teal: "#1A9D8B",
    tealBright: "#41C6B5",
    tealDeep: "#0A6055",
    mint: "#5EEAD4",
    ink: "#072826",
    inkSoft: "#0B2B2B",
    text: "#0B2E2C",
    textSub: "#3E6863",
    textMute: "#6B8D88",
    onDark: "#F2FFFD",
    onDarkSub: "rgba(198,228,223,0.78)",
    onDarkMute: "rgba(174,202,198,0.55)",
    ok: "#0A7345",
    okSoft: "rgba(12,138,82,0.13)",
    warn: "#87510A",
    warnSoft: "rgba(214,158,46,0.18)",
    danger: "#A93A33",
    dangerSoft: "rgba(196,68,61,0.13)",
    hairline: "rgba(11,46,44,0.07)",
}
export const SITE_BG = "#EAFFFC"

export const FONT = {
    display: "'General Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    numeric: "'Atkinson Hyperlegible', 'General Sans', sans-serif",
}

/** RADIUS — six steps, absorbing the 35 ad-hoc values across the two files.
 *  Every one of those rounded to its nearest step here; nothing moves by more
 *  than 3px, and cards that were meant to match now do. */
export const R = { xs: 8, sm: 12, md: 16, lg: 22, xl: 28, pill: 999 }

/** SPACE — 4px base. The files already cluster on multiples of 4; this just
 *  names them so a gap is chosen rather than typed. */
export const S = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 }

/** TYPE — the scale the dashboard actually uses, which is where this app's
 *  sizes were supposed to come from and had drifted away from.
 *
 *  Audited from PayvitDashboard.tsx: its ONLY size above 26 is the 38px
 *  balance figure. Everything else tops out at 26, section headings sit at
 *  20–24, and the bulk of the interface lives between 10.5 and 15. This file
 *  had grown a 42, a 40, two 34s, two 32s, a 29, a 28 and a 27 — nine
 *  competing "big" steps, so nothing read as more important than anything
 *  else and every screen shouted.
 *
 *  Smaller, but never lighter: every step here is set at 800. Hierarchy comes
 *  from weight and colour, which cost no space, rather than from size, which
 *  costs all of it. */
export const F = {
    /** The single dominant figure on a screen. Never two on one screen.
     *  Re-audited against the real dashboard reference (its actual max is
     *  38, used only for the one true balance figure) — 34 ran every big
     *  number on this scale close to that ceiling regardless of whether it
     *  was the real thing or a secondary total. Trimmed toward the
     *  reference's own smaller "big number" tier (it also uses 30) rather
     *  than the ceiling, since every other role sharing this token isn't
     *  the screen's single dominant figure. */
    hero: 30,
    /** Screen title, or a secondary large amount. Re-audited: the
     *  reference's headings cluster at 20-22, not 26 — this token was
     *  sitting a full tier above what it was modeled on. */
    display: 22,
    /** Section heading. Already matches the reference's most common
     *  heading size exactly (20) — left unchanged. */
    title: 20,
    /** Emphasised value inside a card — a total, a rate, a reference. */
    sub: 17,
    /** Reading size. */
    body: 14.5,
    /** Supporting line under a title. */
    small: 13,
    /** Uppercase section labels. No tracking — see .lbl in styles. */
    micro: 11,
}

/** MOTION — the two existing tokens, plus names for the twelve loose
 *  durations. Every raw `duration: 0.xx` in the current code maps onto one of
 *  these four; nothing new was introduced to make them fit. */
export const EASE = [0.22, 1, 0.36, 1] as const
export const SPRING = { type: "spring" as const, stiffness: 420, damping: 36, mass: 0.8 }
export const D = {
    /** press feedback, mask swaps — must beat the 100ms perception threshold */
    instant: 0.12,
    /** chip and pill state, small cross-fades */
    quick: 0.18,
    /** the default: page, sheet, list */
    base: 0.26,
    /** hero and first paint only */
    slow: 0.42,
}
export const T = {
    instant: { duration: D.instant, ease: EASE },
    quick: { duration: D.quick, ease: EASE },
    base: { duration: D.base, ease: EASE },
    slow: { duration: D.slow, ease: EASE },
}

/** BREAKPOINTS — one set. The dashboard used 1280/1440/1600 and the flow
 *  768/1024, so the two halves of the product reflowed at different widths. */
export const BP = { tablet: 768, desktop: 1024, wide: 1400 }

/** GLASS — three tiers, one recipe each.
 *
 *  The current build has nine different backdrop-filter recipes across two
 *  parallel class families. The recipe itself was never the problem; the
 *  count was. Tier is chosen by what the surface DOES, so depth reads without
 *  thinking: chip sits on a card, card sits on the page, sheet sits over
 *  everything. */
export const GLASS_CSS = `
.g{position:relative;isolation:isolate}

/* Tier 1 — chip. Rides on top of another glass surface, so it is the
   lightest: it must not double the blur underneath it. */
.g-chip{
  background:linear-gradient(158deg,rgba(255,255,255,.58) 0%,rgba(240,255,252,.38) 100%);
  border:1px solid rgba(255,255,255,.5);
  border-top-color:rgba(255,255,255,.82);
  backdrop-filter:blur(14px) saturate(170%);
  -webkit-backdrop-filter:blur(14px) saturate(170%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.7);
}

/* Tier 2 — card. The workhorse. This is the recipe the current build already
   gets right; it is now the only card recipe. */
.g-card{
  background:linear-gradient(158deg,rgba(255,255,255,.80) 0%,rgba(240,255,252,.56) 52%,rgba(228,250,245,.48) 100%);
  border:1px solid rgba(255,255,255,.60);
  border-top-color:rgba(255,255,255,.98);
  border-left-color:rgba(255,255,255,.84);
  backdrop-filter:blur(22px) saturate(190%);
  -webkit-backdrop-filter:blur(22px) saturate(190%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.95),
    inset 0 -1px 0 rgba(11,46,44,.05),
    0 22px 46px -22px rgba(7,40,38,.30),
    0 3px 8px -3px rgba(7,40,38,.13);
}

/* Tier 3 — sheet. Over everything, so it blurs hardest and throws the
   longest shadow. The only tier allowed to lift more than a card. */
.g-sheet{
  background:linear-gradient(168deg,rgba(255,255,255,.92) 0%,rgba(240,255,252,.80) 100%);
  border:1px solid rgba(255,255,255,.72);
  border-top-color:rgba(255,255,255,1);
  backdrop-filter:blur(30px) saturate(190%);
  -webkit-backdrop-filter:blur(30px) saturate(190%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,1),
    0 -2px 0 rgba(11,46,44,.04),
    0 -30px 70px -30px rgba(7,40,38,.38);
}

/* Dark tier, for the hero and the card page — same construction inverted. */
.g-dark{
  background:linear-gradient(158deg,rgba(12,84,75,.96) 0%,rgba(8,58,52,.98) 60%,rgba(7,40,38,1) 100%);
  border:1px solid rgba(255,255,255,.10);
  border-top-color:rgba(255,255,255,.20);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.16),
    inset 0 -1px 0 rgba(0,0,0,.30),
    0 26px 54px -24px rgba(7,40,38,.55),
    0 4px 12px -6px rgba(7,40,38,.4);
}

/* One fallback, not nine. Where backdrop-filter is unsupported the glass
   becomes opaque rather than transparent — a see-through panel with no blur
   is unreadable, which is worse than no glass at all. */
@supports not ((backdrop-filter:blur(1px)) or (-webkit-backdrop-filter:blur(1px))){
  .g-chip{background:rgba(247,255,253,.94)}
  .g-card{background:rgba(250,255,254,.97)}
  .g-sheet{background:rgba(252,255,254,.99)}
}
`
